
import Vue from "vue";
import Component from "vue-class-component";
import noraAvatar from "@/assets/support-assistant/nora-avatar.png";
import ConversationSidebar from "./components/ConversationSidebar.vue";
import {
  archiveSupportAssistantConversation,
  createSupportAssistantConversation,
  getSupportAssistantConversation,
  getSupportAssistantConversations,
  sendSupportAssistantMessage
} from "@/api";
import {
  SupportAssistantConversation,
  SupportAssistantMessage
} from "@/interfaces/SupportAssistant.interfaces";

const POLL_INTERVAL_MS = 2500;

@Component({
  name: "SupportAssistant",
  components: { ConversationSidebar }
})
export default class SupportAssistant extends Vue {
  readonly noraAvatar = noraAvatar;
  conversations: SupportAssistantConversation[] = [];
  messages: SupportAssistantMessage[] = [];
  activeConversationId: string | null = null;
  draftMessage = "";
  loadingConversations = false;
  loadingConversation = false;
  sending = false;
  archiving = false;
  errorMessage = "";
  mobileHistoryOpen = false;
  archiveDialogOpen = false;
  conversationToArchive: SupportAssistantConversation | null = null;
  pollTimer: number | null = null;
  requestVersion = 0;

  readonly suggestions = [
    "Why can't this talent see available shifts?",
    "What happened with this account?",
    "Why was this shift cancelled?",
    "Can you explain this invoice?"
  ];

  get canSend(): boolean {
    return (
      this.draftMessage.trim().length > 0 &&
      !this.sending &&
      !this.hasPendingMessage()
    );
  }

  async mounted(): Promise<void> {
    await this.loadConversations();
  }

  beforeDestroy(): void {
    this.stopPolling();
    this.requestVersion += 1;
  }

  async loadConversations(): Promise<void> {
    this.loadingConversations = true;
    try {
      const response = await getSupportAssistantConversations();
      this.conversations = response.conversations;
    } catch (error) {
      this.showError(error, "Your conversation history could not be loaded.");
    } finally {
      this.loadingConversations = false;
    }
  }

  async selectConversation(conversationId: string): Promise<void> {
    this.mobileHistoryOpen = false;
    if (
      conversationId === this.activeConversationId &&
      this.messages.length > 0
    ) {
      return;
    }

    this.activeConversationId = conversationId;
    this.messages = [];
    this.stopPolling();
    await this.loadConversation(conversationId, true);
  }

  async loadConversation(
    conversationId: string,
    showLoader = false
  ): Promise<void> {
    const requestVersion = ++this.requestVersion;
    if (showLoader) {
      this.loadingConversation = true;
    }

    try {
      const response = await getSupportAssistantConversation(conversationId);
      if (
        requestVersion !== this.requestVersion ||
        conversationId !== this.activeConversationId
      ) {
        return;
      }

      this.messages = response.messages;
      this.upsertConversation(response.conversation);
      await this.scrollToBottom();

      if (this.hasPendingMessage()) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    } catch (error) {
      if (requestVersion === this.requestVersion) {
        this.stopPolling();
        this.showError(error, "This conversation could not be opened.");
      }
    } finally {
      if (requestVersion === this.requestVersion) {
        this.loadingConversation = false;
      }
    }
  }

  startNewChat(): void {
    this.requestVersion += 1;
    this.stopPolling();
    this.activeConversationId = null;
    this.messages = [];
    this.draftMessage = "";
    this.errorMessage = "";
    this.mobileHistoryOpen = false;
  }

  useSuggestion(suggestion: string): void {
    this.draftMessage = suggestion;
    this.$nextTick(() => {
      const composer = this.$el.querySelector<HTMLTextAreaElement>("textarea");
      composer?.focus();
    });
  }

  async sendMessage(): Promise<void> {
    const message = this.draftMessage.trim();
    if (!message || this.sending) {
      return;
    }

    this.sending = true;
    this.errorMessage = "";
    this.draftMessage = "";

    try {
      let conversationId = this.activeConversationId;
      if (!conversationId) {
        const created = await createSupportAssistantConversation();
        conversationId = created.conversation.id;
        this.activeConversationId = conversationId;
        this.upsertConversation(created.conversation);
      }

      const response = await sendSupportAssistantMessage(
        conversationId,
        message
      );
      this.upsertConversation(response.conversation);
      await this.loadConversation(conversationId);
      this.startPolling();
      await this.loadConversations();
    } catch (error) {
      this.draftMessage = message;
      this.showError(
        error,
        "Your message could not be sent. Please try again."
      );
    } finally {
      this.sending = false;
    }
  }

  requestArchive(conversation: SupportAssistantConversation): void {
    this.conversationToArchive = conversation;
    this.archiveDialogOpen = true;
  }

  cancelArchive(): void {
    this.archiveDialogOpen = false;
    this.conversationToArchive = null;
  }

  async confirmArchive(): Promise<void> {
    if (!this.conversationToArchive || this.archiving) {
      return;
    }

    const conversationId = this.conversationToArchive.id;
    this.archiving = true;
    try {
      await archiveSupportAssistantConversation(conversationId);
      this.conversations = this.conversations.filter(
        item => item.id !== conversationId
      );
      if (this.activeConversationId === conversationId) {
        this.startNewChat();
      }
      this.cancelArchive();
    } catch (error) {
      this.showError(error, "The conversation could not be archived.");
    } finally {
      this.archiving = false;
    }
  }

  startPolling(): void {
    if (this.pollTimer !== null || !this.activeConversationId) {
      return;
    }

    this.pollTimer = window.setInterval(() => {
      const conversationId = this.activeConversationId;
      if (conversationId) {
        void this.loadConversation(conversationId);
      }
    }, POLL_INTERVAL_MS);
  }

  stopPolling(): void {
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  hasPendingMessage(): boolean {
    return this.messages.some(message => message.status === "pending");
  }

  upsertConversation(conversation: SupportAssistantConversation): void {
    const index = this.conversations.findIndex(
      item => item.id === conversation.id
    );
    if (index >= 0) {
      this.$set(this.conversations, index, conversation);
    } else {
      this.conversations.unshift(conversation);
    }

    this.conversations.sort(
      (left, right) =>
        new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
    );
  }

  formatDateTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  }

  formatMessageContent(value: string): string {
    // NORA returns lightweight Markdown, but support chat intentionally
    // renders plain text so database/log content can never become HTML.
    return value
      .replace(/^#{1,6}\s+/gm, "")
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      .replace(/__([^_]+)__/g, "$1")
      .replace(/`([^`]+)`/g, "$1");
  }

  slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  }

  showError(_error: unknown, fallback: string): void {
    this.errorMessage = fallback;
  }

  async scrollToBottom(): Promise<void> {
    await this.$nextTick();
    const messageArea = this.$refs.messageArea as HTMLElement | undefined;
    if (messageArea) {
      messageArea.scrollTop = messageArea.scrollHeight;
    }
  }
}
